<script setup>
import { computed, ref } from 'vue';
import { fromUnixTime, format } from 'date-fns';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType, createdAt } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

// El cliente borró este mensaje desde su WhatsApp (Victor 2026-06-06): el CRM
// conserva el texto y solo agrega un sello al pie. La marca la pone WahaRevokeJob.
const isDeletedByContact = computed(
  () => !!contentAttributes.value?.deletedByContact
);

// Algunos borrados llegan como lápida de WAHA ("Este mensaje fue eliminado en
// WhatsApp") sin marca ni texto original: los tratamos igual, mostrando solo el sello.
const isWahaDeletedPlaceholder = computed(() =>
  /eliminado en WhatsApp/i.test(content.value || '')
);

const isDeleted = computed(
  () => isDeletedByContact.value || isWahaDeletedPlaceholder.value
);

// Si es la lápida de WAHA no hay texto que conservar: ocultamos el contenido crudo.
const hasOriginalBody = computed(
  () => !!renderContent.value && !isWahaDeletedPlaceholder.value
);

// Hora del borrado (o, en su defecto, la del mensaje) para el pie "· 2:14 PM".
const deletedTime = computed(() => {
  const ts = contentAttributes.value?.deletedByContactAt || createdAt.value;
  return ts ? format(fromUnixTime(ts), 'h:mm a') : '';
});

// Llamada de WhatsApp (Victor 2026-06-06): WAHA entrega la llamada entrante como
// texto "📞🔔 Llamada de voz entrante". La mostramos como una burbuja de llamada
// profesional en vez del texto plano. (WhatsApp/WAHA no da duración ni salientes.)
const isCallEvent = computed(() => {
  const c = content.value || '';
  return (/📞/.test(c) && /llamada/i.test(c)) || /llamada de (voz|v[ií]deo) entrante/i.test(c);
});

const isVideoCall = computed(() => /v[ií]deo/i.test(content.value || ''));

const callTitle = computed(() =>
  isVideoCall.value ? 'Videollamada entrante' : 'Llamada de voz entrante'
);

const callTime = computed(() =>
  createdAt.value ? format(fromUnixTime(createdAt.value), 'h:mm a') : ''
);

// Respuesta a un estado de WhatsApp / historia de Instagram (Victor 2026-06-08):
// el servidor WAHA mete la miniatura del estado en base64 dentro de "Texto del
// Estado", lo que ensuciaba la burbuja con miles de caracteres. La detectamos y
// la mostramos limpia (estilo cita de estado), descartando el base64.
const statusReply = computed(() => {
  const c = content.value || '';
  if (!/Respuesta al estado|Respuesta a la historia|historia de Instagram/i.test(c)) {
    return null;
  }
  const isIG = /Instagram/i.test(c);
  const mediaRaw = (c.match(/\*\*Tipo:\*\*\s*([^\n*]+)/i)?.[1] || '')
    .trim()
    .toLowerCase();
  const isVideo = /vid/i.test(mediaRaw);
  let mediaLabel = '📷 Estado';
  if (isVideo) mediaLabel = '🎥 Video';
  else if (/imag|foto|photo/i.test(mediaRaw)) mediaLabel = '🖼️ Foto';
  // La respuesta real del cliente es lo que viene DESPUÉS de la línea del estado.
  const lines = c.split('\n');
  const idx = lines.findIndex(l => /Texto del Estado/i.test(l));
  let reply = idx >= 0 ? lines.slice(idx + 1).join('\n') : '';
  reply = reply.replace(/\S{120,}/g, '').trim(); // borra cualquier base64 residual
  return { isIG, isVideo, mediaLabel, reply };
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    class="px-4 py-3"
    data-bubble-name="text"
    :hide-meta="isCallEvent"
  >
    <!-- Burbuja de llamada de WhatsApp (Propuesta 2, Victor 2026-06-06): ícono en
         círculo + título + estado con la hora. Deja registro en el CRM de que el
         cliente llamó y cuándo. -->
    <div v-if="isCallEvent" class="flex gap-3 items-center min-w-[210px]">
      <div
        class="flex justify-center items-center rounded-full size-10 shrink-0 bg-[#c9a35a]/[0.14] border border-[#c9a35a]/40"
      >
        <Icon icon="i-ph-phone-incoming" class="size-5 text-[#c9a35a]" />
      </div>
      <div class="flex flex-col">
        <span class="text-sm font-medium text-n-slate-12">{{ callTitle }}</span>
        <span class="text-xs text-n-slate-11">
          Recibida<template v-if="callTime"> · {{ callTime }}</template>
        </span>
      </div>
    </div>
    <!-- Respuesta a un estado de WhatsApp / historia de Instagram (P1, Victor
         2026-06-08): cita del estado con barrita de color (verde WhatsApp /
         degradado Instagram), miniatura con el tipo, y debajo la respuesta real
         del cliente. Nunca muestra el volcado base64. -->
    <div
      v-else-if="statusReply"
      class="flex flex-col gap-2 min-w-[210px] max-w-[280px]"
    >
      <div
        class="flex gap-2.5 items-stretch rounded-lg overflow-hidden bg-white/[0.03]"
      >
        <div
          class="w-[3px] shrink-0"
          :class="
            statusReply.isIG
              ? 'bg-gradient-to-b from-[#dc2743] to-[#bc1888]'
              : 'bg-[#25d366]'
          "
        />
        <div
          class="relative w-11 shrink-0 flex items-center justify-center bg-gradient-to-br from-[#3a2e20] to-[#15110b] text-white text-sm"
        >
          {{ statusReply.isVideo ? '▶' : '🖼️' }}
        </div>
        <div class="py-1.5 pr-2 flex flex-col justify-center min-w-0">
          <span
            class="text-[11px] font-semibold"
            :class="statusReply.isIG ? 'text-[#f0a6c4]' : 'text-[#7ee2a8]'"
          >
            {{
              statusReply.isIG
                ? '📸 Respondió a tu historia'
                : '📣 Respondió a tu estado'
            }}
          </span>
          <span class="text-[11.5px] text-n-slate-11 mt-0.5">{{
            statusReply.mediaLabel
          }}</span>
        </div>
      </div>
      <div
        v-if="statusReply.reply"
        class="text-sm text-n-slate-12 whitespace-pre-wrap break-words"
      >
        {{ statusReply.reply }}
      </div>
    </div>
    <div v-else class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <div v-if="hasOriginalBody || !isDeleted">
        <FormattedContent v-if="renderContent" :content="renderContent" />
      </div>
      <!-- Sello de borrado por el cliente — "Propuesta A" (Victor 2026-06-06): el
           texto original se conserva completo y al pie queda una firma discreta con
           línea fina + ícono dorado. La marca la pone WahaRevokeJob. -->
      <div v-if="isDeleted" :class="hasOriginalBody ? '-mt-1' : ''">
        <div v-if="hasOriginalBody" class="border-t border-white/[0.08] mb-1.5" />
        <div
          class="flex items-center gap-1.5 text-[11px] italic text-n-slate-11"
        >
          <svg
            class="w-3 h-3 flex-none text-[#c9a35a]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
          </svg>
          Eliminado por el cliente
          <template v-if="deletedTime"> · {{ deletedTime }}</template>
        </div>
      </div>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
